import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type SelectType from 'react-select/dist/declarations/src/Select';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Select from '../../components/Select';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { TopicPrivacy } from '../../graphql/generated/graphql';
import topicPolicyCheck, {
  MEMBER_EDITABLE_NONE,
  TopicPolicyAction,
} from '../../topic/topicPolicyCheck';
import type {
  AllTopicsTopic,
  AllTopicsTreeListTopic,
} from '../../topic/useAllTopics';
import type { TopicHierarchicalOptionType } from '../../topic/useTopicHierarchicalSelectProps';
import useTopicHierarchicalSelectProps from '../../topic/useTopicHierarchicalSelectProps';
import { UIActions } from '../../ui/uiSlice';
import { usePublicContentPermissions } from '../../utils/useAllowPublicContentBy';
import styles from './PostTopicAdd.module.scss';

const TOPIC_FRAGMENT = gql(`
  fragment PostTopicAdd_topic on PublicTopicFields {
    id
  }
`);

interface PostTopicAddProps {
  onAdd: (topic: AllTopicsTopic) => void;
  onCreate: (name: string) => void;
  postTopics: DocumentType<typeof TOPIC_FRAGMENT>[];
  large?: boolean;
}

const PostTopicAdd = ({
  onAdd,
  onCreate,
  postTopics,
  large,
}: PostTopicAddProps) => {
  const selectRef = useRef<SelectType<TopicHierarchicalOptionType>>(null);
  const currentUser = useAppSelector((state) => state.session.user);
  const isOpen = useAppSelector((state) => state.ui.isPostTopicSelectOpen);
  const dispatch = useAppDispatch();
  const { isPermittedToPublicizePost } = usePublicContentPermissions();

  useEffect(() => {
    if (isOpen) selectRef.current?.focus();
  }, [isOpen]);

  const isTopicDisabled = useMemo(() => {
    const selectedIdSet = new Set(postTopics.map((topic) => topic.id));

    return (topic: AllTopicsTreeListTopic) =>
      selectedIdSet.has(topic.id) ||
      !topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser) ||
      (!isPermittedToPublicizePost && topic.privacy === 'PUBLIC');
  }, [postTopics, currentUser, isPermittedToPublicizePost]);

  const getExtra = useMemo(
    () => (topic: AllTopicsTreeListTopic) => {
      let secondary = null;

      if (!topicPolicyCheck(TopicPolicyAction.EditPost, topic, currentUser)) {
        secondary =
          topic.memberEditable === MEMBER_EDITABLE_NONE
            ? 'Restricted to owners'
            : 'Restricted to members';
      }

      return { secondary };
    },
    [currentUser],
  );

  const getTopicTooltip = useCallback(
    (topic: { privacy: TopicPrivacy }) =>
      !isPermittedToPublicizePost && topic.privacy === 'PUBLIC'
        ? 'Only admins can add posts to public topics'
        : undefined,
    [isPermittedToPublicizePost],
  );

  const topicSelectProps = useTopicHierarchicalSelectProps({
    getExtra,
    getValue: (t) => t,
    isDisabled: isTopicDisabled,
    tooltip: getTopicTooltip,
    skip: !isOpen,
  });

  return (
    <Select<TopicHierarchicalOptionType>
      selectRef={selectRef}
      {...{
        ...topicSelectProps,
        components: {
          ...topicSelectProps.components,
          DropdownIndicator: () => false,
        },
      }}
      blurInputOnSelect
      className={classNames(styles.select, { [styles.large]: large })}
      creatable
      isClearable={false}
      onChange={(value, meta) => {
        if (!value) {
          return;
        }
        if (meta.action === 'create-option') {
          // @ts-expect-error value is string when new option is created
          onCreate(value);
        } else if (meta.action === 'select-option') {
          onAdd(value);
        }
      }}
      placeholder="Add a topic…"
      formatCreateLabel={(promptLabel: string) =>
        `Create new topic "${promptLabel}"`
      }
      value={null}
      menuIsOpen={isOpen}
      onMenuOpen={() => dispatch(UIActions.changePostTopicSelectOpen(true))}
      onMenuClose={() => dispatch(UIActions.changePostTopicSelectOpen(false))}
      controlShouldRenderValue={false}
      aria-label="Add a topic to this post"
    />
  );
};

export default PostTopicAdd;
